import { GetConstraintsResponse } from 'api/typegen/application/constraints/get';
import { QueryStatus } from '@tanstack/react-query';
import { useEffect } from 'react';
import useAuth from '../../../hooks/useAuth';
import useLoanAmount from '../LoanApplicationProcessProvider/useLoanAmount';
import useLoanClientConstraintsQuery from '../../../hooks/useLoanClientConstraintsQuery';
import useLoanConstraintsQuery from 'ui/hooks/useLoanConstraintsQuery';

export interface UseAmountConstraints {
  status: QueryStatus;
  amountConstraints: GetConstraintsResponse['amountInterval'];
}

const useAmountConstraints = (): UseAmountConstraints => {
  const loanClientConstraintsQuery = useLoanClientConstraintsQuery();
  const loanConstraintsQuery = useLoanConstraintsQuery();
  const { loanAmount, setLoanAmount } = useLoanAmount();
  const { token } = useAuth();

  const userIsLogged = Boolean(token);

  const defaultValue = userIsLogged
    ? loanClientConstraintsQuery.loanClientConstraints?.amountInterval
        ?.defaultValue
    : loanConstraintsQuery.loanConstraints?.amountInterval?.defaultValue;

  const maxLoanAmount = userIsLogged
    ? loanClientConstraintsQuery.loanClientConstraints?.amountInterval?.max
    : loanConstraintsQuery.loanConstraints?.loanLimits?.[1] ||
      loanConstraintsQuery.loanConstraints?.amountInterval?.max;

  const minLoanAmount = userIsLogged
    ? loanClientConstraintsQuery.loanClientConstraints?.amountInterval?.min
    : loanConstraintsQuery.loanConstraints?.amountInterval?.min;

  const status = userIsLogged
    ? loanClientConstraintsQuery.status
    : loanConstraintsQuery.status;

  useEffect(() => {
    if (
      maxLoanAmount !== undefined &&
      minLoanAmount !== undefined &&
      loanAmount !== null
    ) {
      const invalidAmount =
        loanAmount > maxLoanAmount || loanAmount < minLoanAmount;
      if (status === 'success' && invalidAmount) {
        if (loanAmount > maxLoanAmount) setLoanAmount(maxLoanAmount);
        else if (loanAmount < minLoanAmount) setLoanAmount(minLoanAmount);
      }
    }
  }, [status, loanAmount]);

  return {
    status: userIsLogged
      ? loanClientConstraintsQuery.status
      : loanConstraintsQuery.status,
    amountConstraints: {
      ...loanConstraintsQuery.loanConstraints?.amountInterval,
      defaultValue,
      min: minLoanAmount,
      max: maxLoanAmount,
    },
  };
};

export default useAmountConstraints;
